/**
 * `/runs/:id` — one agent run in full.
 *
 * ---------------------------------------------------------------------------
 * THE SNAPSHOT IS SORTED BY WHAT WE DO NOT KNOW
 * ---------------------------------------------------------------------------
 * The snapshot is rendered dimension by dimension, each with its own status —
 * and grouped by epistemic class with the UNMEASURED ones first. That ordering
 * is the argument of the page: a dimension that is `unproven` or `error` is not
 * comparable across sweeps, which is very often exactly why the delta layer
 * fired and the agent ran at all. Sorting alphabetically, as this page used to,
 * buries the cause of the run among thirty rows of `ok`.
 *
 * The gates get the same treatment. `proven` / `unproven` / `blocked` is the
 * same four-way vocabulary one level down, and it explains what the sweep was
 * ALLOWED to conclude.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { formatUsd, fullTime, relativeTime } from '../lib/format';
import { type Epistemic, epistemicFor, explain, labelFor } from '../lib/signal';
import type { Signal } from '../lib/signal';
import { Card, Eyebrow, Ident, KeyValue, Placeholder, SignalPanel, Skeleton, StatusPill } from '../components/primitives';
import { Icon } from '../components/icons';

export interface RunData {
  generatedAt: string;
  id: string;
  sweep: string;
  sweepId: number | null;
  ts: string;
  model: string | null;
  verdict: string | null;
  summary: string | null;
  error: string | null;
  cost: { inputTokens: number | null; outputTokens: number | null; usd: number | null };
  sdkSessionId: string | null;
  status: Signal<Record<string, unknown>>;
  snapshot: Signal<{
    sweep: string;
    collectedAt: string;
    dimensions: Record<string, Signal<unknown>>;
    gates: Record<string, string>;
  }>;
  checks: { id: string; ok: boolean; value: unknown; note: string | null }[];
}

const GROUP_ORDER: Epistemic[] = ['unmeasured', 'unmeasurable', 'measured'];

const GROUP_TITLE: Record<Epistemic, string> = {
  unmeasured: 'not measured',
  unmeasurable: 'cannot be measured',
  measured: 'measured',
};

/*
 * One clause each. The full argument lives in the reading key; repeating it
 * over three groups on a page that also carries a JSON blob is how a run
 * transcript becomes unreadable.
 */
const GROUP_NOTE: Record<Epistemic, string> = {
  unmeasured: 'not comparable against the previous snapshot; cannot support a GREEN verdict',
  unmeasurable: 'never queried — phase 0 proved these cannot return data today',
  measured: 'read this cycle; an `empty` here is a gate-proven nothing, not a zero',
};

export function RunPage({
  data,
  navigate,
}: {
  data: RunData | null;
  navigate: (to: string) => void;
}): ReactNode {
  if (data === null) {
    return <Skeleton label="this run" shapes={['h-64']} />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <button
        type="button"
        onClick={() => {
          navigate('/');
        }}
        className="inline-flex items-center gap-2 rounded-lg border border-line px-3 text-xs text-ink-3 transition-colors hover:border-line-strong hover:text-ink"
      >
        <Icon name="back" size={14} /> overview
      </button>

      <Card
        title={`${data.sweep} run`}
        subtitle={<Ident className="break-all">{data.id}</Ident>}
        right={
          data.verdict === null ? (
            <StatusPill status="unproven">no verdict</StatusPill>
          ) : (
            <StatusPill status={data.verdict} />
          )
        }
      >
        {data.summary !== null && (
          <p className="mb-3 text-sm leading-relaxed text-ink">{data.summary}</p>
        )}
        {data.error !== null && (
          <p className="mb-4 flex items-start gap-2 rounded-lg border border-bad/40 px-3 py-2.5 text-xs leading-relaxed text-bad">
            <Icon name="error" size={14} className="mt-px" />
            {data.error}
          </p>
        )}
        <dl className="divide-y divide-line border-t border-line">
          <KeyValue label="when">
            <span className="num" title={fullTime(data.ts)}>
              {relativeTime(data.ts)}
            </span>
          </KeyValue>
          <KeyValue label="model">
            {data.model ?? <span className="text-ink-3">not recorded</span>}
          </KeyValue>
          <KeyValue label="tokens">
            {data.cost.inputTokens === null && data.cost.outputTokens === null ? (
              <span className="text-ink-3">not recorded</span>
            ) : (
              <span className="num">
                {data.cost.inputTokens ?? 0} in / {data.cost.outputTokens ?? 0} out
              </span>
            )}
          </KeyValue>
          <KeyValue label="cost">
            <span className="num">{formatUsd(data.cost.usd)}</span>
          </KeyValue>
          <KeyValue label="SDK session">
            {data.sdkSessionId === null ? (
              <span className="text-2xs text-ink-3">none — resume falls back to the case file</span>
            ) : (
              <Ident className="break-all">{data.sdkSessionId}</Ident>
            )}
          </KeyValue>
        </dl>
      </Card>

      {data.checks.length > 0 && (
        <Card title="Checks" icon="checks" subtitle="what the agent verified">
          <ul className="space-y-1.5">
            {data.checks.map((check) => (
              <li
                key={check.id}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-surface-2 px-2.5 py-2"
              >
                <Ident className="min-w-0 flex-1 break-all leading-snug">{check.id}</Ident>
                {check.value !== null && check.value !== undefined && (
                  <span className="mono shrink-0 text-2xs text-ink-3">
                    {JSON.stringify(check.value)}
                  </span>
                )}
                <StatusPill status={check.ok ? 'ok' : 'error'} size="sm" title={check.note}>
                  {check.ok ? 'ok' : 'failed'}
                </StatusPill>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <SignalPanel signal={data.snapshot} emptyTitle="no snapshot for this run">
        {(snapshot) => <SnapshotPanel snapshot={snapshot} />}
      </SignalPanel>

      <Card title="Status JSON" subtitle="what the agent returned, after schema validation">
        <SignalPanel signal={data.status} emptyTitle="no status blob for this run">
          {(status) => (
            <pre className="mono max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-line bg-surface-2 p-3 text-[11px] leading-relaxed text-ink-2">
              {JSON.stringify(status, null, 2)}
            </pre>
          )}
        </SignalPanel>
      </Card>
    </div>
  );
}

function SnapshotPanel({
  snapshot,
}: {
  snapshot: {
    sweep: string;
    collectedAt: string;
    dimensions: Record<string, Signal<unknown>>;
    gates: Record<string, string>;
  };
}): ReactNode {
  const grouped = useMemo(() => {
    const groups: Record<Epistemic, [string, Signal<unknown>][]> = {
      measured: [],
      unmeasured: [],
      unmeasurable: [],
    };
    for (const [name, signal] of Object.entries(snapshot.dimensions)) {
      groups[epistemicFor(signal.status)].push([name, signal]);
    }
    for (const key of GROUP_ORDER) {
      groups[key].sort(([a], [b]) => a.localeCompare(b));
    }
    return groups;
  }, [snapshot.dimensions]);

  const unknown = grouped.unmeasured.length + grouped.unmeasurable.length;
  const total = Object.keys(snapshot.dimensions).length;

  return (
    <Card
      title="Snapshot"
      icon="snapshot"
      subtitle="the deterministic half of the sweep — what the delta layer compares"
      right={
        <StatusPill status={unknown === 0 ? 'ok' : 'unproven'}>
          {unknown === 0
            ? `${String(total)} measured`
            : `${String(unknown)} of ${String(total)} unknown`}
        </StatusPill>
      }
    >
      <p className="mb-3 text-2xs text-ink-3">collected {fullTime(snapshot.collectedAt)}</p>

      <div className="space-y-4">
        {GROUP_ORDER.filter((group) => grouped[group].length > 0).map((group) => (
          <div key={group}>
            <div className="flex flex-wrap items-baseline gap-x-2.5">
              <Eyebrow>
                {GROUP_TITLE[group]} · {grouped[group].length}
              </Eyebrow>
              <p className="text-2xs leading-relaxed text-ink-3">{GROUP_NOTE[group]}</p>
            </div>
            <ul className="mt-2 space-y-1.5">
              {grouped[group].map(([name, signal]) => {
                const why = explain(signal);
                return (
                  <li
                    key={name}
                    className="rounded-lg border border-line bg-surface-2 px-2.5 py-2"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Ident className="min-w-0 flex-1 break-all leading-snug">{name}</Ident>
                      <StatusPill status={signal.status} size="sm" />
                    </div>
                    {signal.status === 'ok' ? (
                      <pre className="mono mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-all text-[10px] leading-relaxed text-ink-3">
                        {JSON.stringify(signal.value)}
                      </pre>
                    ) : (
                      <p
                        className={`mt-1 text-[11px] leading-relaxed ${
                          signal.status === 'error' ? 'text-bad' : 'text-ink-3'
                        }`}
                      >
                        {why}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {Object.keys(snapshot.gates).length > 0 && (
        <div className="mt-4 border-t border-line pt-3">
          <Eyebrow>gates</Eyebrow>
          <p className="mt-1 max-w-prose text-2xs leading-relaxed text-ink-3">
            A gate whose widened sibling returned nothing makes the dimension it guards{' '}
            <code>unproven</code> (Appendix&nbsp;B rule&nbsp;1).
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(snapshot.gates)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([gate, state]) => (
                <li key={gate}>
                  <StatusPill
                    status={state === 'proven' ? 'ok' : state === 'blocked' ? 'blocked' : 'unproven'}
                    size="sm"
                  >
                    {gate}: {state}
                  </StatusPill>
                </li>
              ))}
          </ul>
        </div>
      )}

      {Object.keys(snapshot.dimensions).length === 0 && (
        <Placeholder status="empty" title="this snapshot carries no dimensions">
          {labelFor('empty')}
        </Placeholder>
      )}
    </Card>
  );
}
